import { motion } from 'framer-motion';
import { hotelPartners } from '../data/hotelPartners';

/**
 * Ultra-luxury hotel partners grid.
 * Logos rendered in white/champagne via CSS filters so 32 brand marks
 * unify into a single cohesive "trust mark" wall — the hallmark of
 * editorial luxury travel publications (Travel+Leisure, Conde Nast).
 */
const HotelPartners = ({ variant = 'full' }) => {
  return (
    <section
      className="section-padding container-padding bg-[#0a0a0a] border-y border-white/5"
      data-testid="hotel-partners-section"
    >
      <div className="max-w-[1920px] mx-auto">
        <div className="text-center mb-16 md:mb-20">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
            In Partnership With
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-primary/90 mb-6">
            The World's Finest Hospitality
          </h2>
          <p className="font-body text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Curated access to the most celebrated hotel brands
            across India and the world — from heritage palaces to modern icons.
          </p>
        </div>

        {/* Logo grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-px bg-white/5">
          {hotelPartners.map((partner, i) => (
            <motion.div
              key={partner.name + i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ delay: (i % 8) * 0.04, duration: 0.6 }}
              className="partner-cell group relative flex flex-col items-center justify-center bg-[#0a0a0a] aspect-[4/3] px-5 py-7 overflow-hidden"
              data-testid={`hotel-partner-${partner.name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '')}`}
            >
              {/* gold accent corner on hover */}
              <span className="absolute top-0 left-0 h-[1px] w-0 bg-accent transition-all duration-500 group-hover:w-full" />
              <span className="absolute bottom-0 right-0 h-[1px] w-0 bg-accent transition-all duration-500 group-hover:w-full" />

              <div className="flex-1 w-full flex items-center justify-center mb-3 bg-white rounded-sm p-3 transition-transform duration-500 group-hover:scale-[1.03]">
                <img
                  src={partner.logo}
                  alt={`${partner.name} logo`}
                  loading="lazy"
                  className="max-h-12 md:max-h-14 w-auto max-w-[85%] object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
                <span
                  className="hidden font-heading text-lg tracking-widest text-accent"
                  aria-hidden="true"
                >
                  {partner.name.charAt(0)}
                </span>
              </div>
              <p className="font-body text-[10px] md:text-xs uppercase tracking-[0.18em] text-primary/60 group-hover:text-accent transition-colors duration-300 text-center leading-tight">
                {partner.name}
              </p>
            </motion.div>
          ))}
        </div>

        {variant === 'full' && (
          <p className="font-accent italic text-center text-muted-foreground/70 text-xs md:text-sm mt-12 max-w-2xl mx-auto">
            Preferred-partner privileges, suite upgrades, and bespoke amenities across
            every stay — reserved for Sarvatra guests.
          </p>
        )}
      </div>
    </section>
  );
};

export default HotelPartners;
